/**
 * Tools to deal with focus management esp. in components.
 */

/**
* Adapted from https://github.com/salesforce/kagekiri
* Get children of a node, including getting slotted content.
* @param {HTMLElement} node Node to check
* @returns {HTMLCollection|HTMLElement[]}
*/
const getChildren = function(node) {
    // is it a slot.
    if (typeof node.assignedElements === 'function') {
        const assigned = node.assignedElements();
        // slotted content or the default
        return assigned.length ? assigned : node.children;
    } else {
        return node.children;
    }
};

/**
 * Is an element hidden.
 * @param {HTMLElement} el Element to check
 * @returns {Boolean}
 */
const isElementHidden = function(el) {
    return !(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
};

/**
 * Is an element focusable.
 * This probably doesn't cover ALL possibilities, but its a decent start.
 * @param {HTMLElement} element Element to check.
 * @param {Boolean} keyboardOnly Are we only looking for tabbable focus.
 * @returns {Boolean}
 */
const isFocusable = function(element, keyboardOnly = false) {
    const tagName = element.tagName;
    // Just in case.
    if (tagName === undefined) {
        return false;
    }
    if (isElementHidden(element)) {
        return false;
    }
    // Will be false-y if not set/available.
    const disabled = element.disabled;
    // Convert to integer or NaN if tabindex is not set.
    const tabIndex = parseInt(element.getAttribute('tabindex'), 10);
    const contentEditable = element.getAttribute('contenteditable');

    switch (tagName) {
        case 'INPUT':
        case 'TEXTAREA':
        case 'SELECT':
        case 'BUTTON':
        case 'A':
            if (tabIndex === -1) {
                return false;
            }
            if (disabled) {
                return false;
            }
            // for input[type=hidden]
            if (element.type === 'hidden') {
                return false;
            }
            if (tagName === 'A' && !element.href) {
                return false;
            }
            return true;
        default:
            if (keyboardOnly && tabIndex >= 0) {
                return true;
            } else if (!keyboardOnly && tabIndex >= -1) {
                return true;
            }
            if (contentEditable === 'true') {
                return true;
            }
    }
    return false;
};

/**
 * Get focusable elements recursively.
 * @param {HTMLElement} element Element to get children of
 * @param {Boolean} keyboardOnly Only tabbable elements?
 * @param {Boolean} single Only get the first one.
 * @returns {HTMElement[]}
 */
const getFocusableChildren = function(element, keyboardOnly = false, single = false) {
    let focusables = [];
    const children = Array.from(getChildren(element));
    if (children.length === 0) {
        return focusables;
    }
    for (const child of children) {
        // check if element is focusable.
        if (isFocusable(child, keyboardOnly)) {
            focusables.push(child);
            if (single) {
                return focusables;
            }
        }
        const childFocusables = getFocusableChildren(child, keyboardOnly, single);
        if (single && childFocusables.length === 1) {
            return childFocusables;
        }
        focusables = focusables.concat(childFocusables);
    }
    return focusables;
};

/**
 * Get focusable elements in the component
 * Shadow DOM and slotted elements
 * Theoretically in the correct order.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @param {Boolean} keyboardOnly Only tabbable elements?
 * @param {Boolean} single Only get the first one.
 * @returns {HTMLElement[]}
 */
const findFocusables = function(parent, keyboardOnly = false, single = false) {
    return getFocusableChildren(parent, keyboardOnly, single);
};

/**
 * Get the first focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findFirstFocusable = function(parent) {
    return findFocusables(parent, false, true).shift();
};
/**
 * Get the first tabbing focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findFirstTabFocusable = function(parent) {
    return findFocusables(parent, true, true).shift();
};
/**
 * Return last focusable element in parent.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 * @returns {HTMLElement|null}
 */
const findLastTabFocusable = function(parent) {
    const focusables = findFocusables(parent, true);
    return focusables[focusables.length - 1];
};

/**
 * Find first focusable element in parent and focus on it.
 * @param {HTMLElement|ShadowRoot} parent Parent to look in.
 */
const focusFirst = function(parent) {
    const focusable = findFirstFocusable(parent);
    if (focusable) {
        focusable.focus();
    }
};

/**
 * Get focused element.
 * @returns {HTMLElement|null}
 */
const deepActiveElement = function() {
    let a = document.activeElement;
    while (a && a.shadowRoot && a.shadowRoot.activeElement) {
        a = a.shadowRoot.activeElement;
    }
    return a;
};

export {
    findFocusables,
    findFirstFocusable,
    findFirstTabFocusable,
    findLastTabFocusable,
    focusFirst,
    deepActiveElement
};
